// Comportamento dos personagens: para onde cada um vai, dado o status real do agente.
//
//   trabalhando / pensando / esperando  → a mesa dele (esperando = mão levantada)
//   shell (processo em segundo plano)   → sala de servidores, olhando o rack
//   ocioso                              → copa, lounge ou espera, num lugar livre
//
// Puro: sem DOM. Quem anda e desenha é o CampusRenderer.

import { stableHash } from '../../core/hash.js';
import type { Agent } from '../../core/types.js';
import { type RoomKind, type Spot } from '../../core/world.js';
import { roomData } from '../engine/room-data.js';
import type { Pose } from '../engine/character.js';
import type { AgentHome, CampusFloor } from './layout.js';
import { SAIDA_FADE_MS } from './saida.js';

/** Um ponto de uso em coordenadas do mundo (tiles), com a sala onde fica e que tipo de sala é. */
export interface WorldSpot extends Spot {
  room: string;
  area: RoomKind;
}

export function floorSpots(floor: CampusFloor): WorldSpot[] {
  const out: WorldSpot[] = [];
  for (const placed of floor.rooms) {
    for (const s of roomData(placed.map).spots) out.push({ ...s, id: `${placed.id}#${s.id}`, x: placed.x + s.x, y: placed.y + s.y, room: placed.id, area: placed.map.kind });
  }
  return out;
}

/** Sala de projeto sem ninguém por este tempo apaga a luz (luz.ts escurece aos poucos). */
export const DARK_AFTER_MS = 10 * 60 * 1000;

/** Fase da luz: dia, entardecer, noite e amanhecer. */
export type DayPhase = 'day' | 'dusk' | 'night' | 'dawn';

/**
 * Fase da luz na hora dada. `mode` é settings.theme.night: 'on' fixa a noite e 'off' fixa o dia
 * (sem entardecer nem amanhecer); 'auto' segue o relógio.
 */
export function dayPhase(hour: number, mode: 'auto' | 'on' | 'off' = 'auto'): DayPhase {
  if (mode === 'on') return 'night';
  if (mode === 'off') return 'day';
  if (hour >= 19 || hour < 5) return 'night';
  if (hour < 7) return 'dawn';
  if (hour >= 17) return 'dusk';
  return 'day';
}

/** Opacidade de quem está saindo, pelo tempo que falta para sumir (1 = inteiro, 0 = sumiu). */
export function leaveAlpha(remainingMs: number): number {
  return Math.min(1, Math.max(0, remainingMs / SAIDA_FADE_MS));
}

/**
 * Onde um personagem aparece no andar: a porta da sala do pai (subagente nasce ali) ou a
 * ponta do corredor. A mesma ponta é por onde todos entram e saem do andar.
 */
export function entryOf(floor: CampusFloor, parentRoom?: string): { x: number; y: number } {
  const room = parentRoom ? floor.rooms.find((r) => r.id === parentRoom) : undefined;
  if (room) {
    const door = floor.doorways.find((d) => d.y === room.y + room.h && d.x >= room.x && d.x < room.x + room.w);
    if (door) return door;
  }
  return { x: floor.corridor.x, y: floor.corridor.y + 1 };
}

/** Frases curtas de quem está ocioso e se vê com alguém (fixas: não passam pelo i18n). */
export const CHAT_LINES = ['☕', 'bora?', '👋'] as const;
/** Um sorteio por par e por janela de 1 min; o balão aparece por 4 s, num ponto dessa janela. */
const CHAT_WINDOW_MS = 60_000;
const CHAT_SHOW_MS = 4_000;
/** Distância máxima (em tiles) para dois ociosos se verem. */
const CHAT_REACH = 3;

/**
 * Conversa entre dois ociosos: sorteada por hash do par e da janela de tempo, então é a mesma
 * para o mesmo par no mesmo instante. Em média uma janela em cada quatro, e um balão por vez.
 */
export function idleChat(a: string, b: string, now: number): { speaker: string; text: string } | null {
  const [x, y] = a < b ? [a, b] : [b, a];
  const slot = Math.floor(now / CHAT_WINDOW_MS);
  const h = stableHash(`${x}|${y}|${slot}`);
  if (h % 4 !== 0) return null;
  const into = now - slot * CHAT_WINDOW_MS;
  const start = (h >>> 8) % (CHAT_WINDOW_MS - CHAT_SHOW_MS);
  if (into < start || into >= start + CHAT_SHOW_MS) return null;
  return { speaker: h & 0x10 ? x : y, text: CHAT_LINES[(h >>> 16) % CHAT_LINES.length]! };
}

/** Ocioso parado num ponto de copa ou lounge. */
export interface Seated {
  id: string;
  spot: WorldSpot;
}

const CHAT_AREAS = new Set<RoomKind>(['kitchen', 'lounge']);

/**
 * Balões de conversa: dos ociosos sentados perto uns dos outros na copa ou no lounge, um fala
 * (cada um fala no máximo uma vez). Devolve id do agente → frase.
 */
export function idleChats(seated: readonly Seated[], now: number): Map<string, string> {
  const out = new Map<string, string>();
  const list = seated.filter((s) => CHAT_AREAS.has(s.spot.area)).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const p = list[i]!;
      const q = list[j]!;
      if (p.spot.room !== q.spot.room) continue;
      if (Math.abs(p.spot.x - q.spot.x) > CHAT_REACH || Math.abs(p.spot.y - q.spot.y) > CHAT_REACH) continue;
      if (out.has(p.id) || out.has(q.id)) continue;
      const talk = idleChat(p.id, q.id, now);
      if (talk) out.set(talk.speaker, talk.text);
    }
  }
  return out;
}

/**
 * Sala apaga a luz quando ninguém está nela há mais de 10 min. `emptySince` é quando o escritório
 * viu a sala vazia pela primeira vez (undefined = tem gente agora).
 */
export function isRoomDark(emptySince: number | undefined, now: number): boolean {
  return emptySince !== undefined && now - emptySince > DARK_AFTER_MS;
}

/** Mesas de uma sala de projeto, em ordem de leitura (linha, coluna). */
export function desksOf(spots: readonly WorldSpot[], room: string): WorldSpot[] {
  return spots.filter((s) => s.room === room && s.use === 'work').sort((a, b) => a.y - b.y || a.x - b.x);
}

const REST_USES = new Set(['rest', 'eat', 'wait']);
/** Cadeiras gamer e lados da mesa de pingue-pongue: só entram pelas rodas (world/rodas.ts), nunca no descanso solto. */
const SO_DAS_RODAS = /#lounge:(gamer|pingpong)-\d+$/;

/**
 * Escolhe o ponto do agente. `reserved` é spotId → agentId (quem já está indo ou
 * sentado lá); o ponto do próprio agente conta como livre para ele.
 */
export function chooseSpot(agent: Agent, home: AgentHome, spots: readonly WorldSpot[], reserved: ReadonlyMap<string, string>): WorldSpot | undefined {
  const desks = desksOf(spots, home.room);
  const desk = desks.length ? desks[home.desk % desks.length] : undefined;
  if (agent.status === 'working' || agent.status === 'thinking' || agent.status === 'waiting') return desk;
  const free = (s: WorldSpot) => {
    const who = reserved.get(s.id);
    return !who || who === agent.id;
  };
  const pick = (list: WorldSpot[]) => {
    if (!list.length) return undefined;
    const start = stableHash(agent.id) % list.length;
    for (let i = 0; i < list.length; i++) {
      const s = list[(start + i) % list.length]!;
      if (free(s)) return s;
    }
    return undefined;
  };
  if (agent.status === 'shell') {
    return pick(spots.filter((s) => s.use === 'server')) ?? desk;
  }
  return pick(spots.filter((s) => REST_USES.has(s.use) && !SO_DAS_RODAS.test(s.id))) ?? desk;
}

/** A pose de quem chegou ao ponto. */
export function poseAt(agent: Agent, spot: WorldSpot | undefined): Pose {
  if (!spot || spot.kind === 'standing') return agent.status === 'waiting' ? 'raise' : 'idle';
  if (agent.status === 'waiting') return 'sitRaise';
  if (spot.use === 'work' && agent.status === 'working') return 'type';
  return 'sit';
}

/** Animação de uma tela de monitor (ver paintScreen em engine/scene.ts). */
export type ScreenMode = 'off' | 'read' | 'write' | 'terminal' | 'web' | 'thinking' | 'done' | 'alert' | 'saver';

/** Quadros de cada animação e quanto dura cada um. 4 fps nas que se mexem; o protetor é lento. */
export const SCREEN_ANIM: Readonly<Record<ScreenMode, { frames: number; ms: number }>> = {
  off: { frames: 1, ms: 1000 },
  read: { frames: 10, ms: 250 },
  write: { frames: 4, ms: 250 },
  terminal: { frames: 6, ms: 250 },
  web: { frames: 4, ms: 250 },
  thinking: { frames: 3, ms: 250 },
  done: { frames: 1, ms: 1000 },
  alert: { frames: 2, ms: 250 },
  saver: { frames: 4, ms: 1000 },
};

/** Por quanto tempo o ✓ de "concluiu" fica na tela depois da atividade. */
export const DONE_SCREEN_MS = 4000;

const TERMINAL_KINDS = new Set(['bash', 'test', 'install', 'build', 'git']);

/**
 * Animação da tela da mesa de um agente, pelo status dele e pelo tipo da última atividade
 * (`kind`, o de src/adapters/common/describe.ts). `sinceKindMs` é quanto tempo faz que a
 * atividade chegou. Sem agente sentado, a tela fica apagada.
 */
export function screenMode(status: Agent['status'] | undefined, kind?: string, sinceKindMs = Infinity): ScreenMode {
  if (!status) return 'off';
  if (status === 'waiting') return 'alert';
  if (status === 'thinking') return 'thinking';
  if (status === 'shell') return 'saver';
  if (status === 'idle') return kind === 'done' && sinceKindMs < DONE_SCREEN_MS ? 'done' : 'saver';
  // working: a ferramenta decide o que aparece na tela
  if (kind === 'write') return 'write';
  if (kind && TERMINAL_KINDS.has(kind)) return 'terminal';
  if (kind === 'web') return 'web';
  if (kind === 'thinking') return 'thinking';
  // read, search e o resto (tarefa, plano, outro): texto rolando
  return 'read';
}

/** Quadro da animação agora. Com movimento reduzido (prefers-reduced-motion) fica sempre no primeiro. */
export function screenFrame(mode: ScreenMode, now: number, reduced = false): number {
  const { frames, ms } = SCREEN_ANIM[mode];
  return reduced ? 0 : Math.floor(now / ms) % frames;
}
